/**
 * Agenda — grilla horaria real (Día/Semana), reemplaza la lista plana anterior.
 * Flujo de referencia: Booking Hub app.agenda.tsx (semana/día, grid 08-19,
 * tabs de profesional, KPIs client-side) — adaptado a los endpoints y modelo
 * de sm_bookings de Food Market, no portado literal.
 */
import { createFileRoute } from "@tanstack/react-router";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { CrudShellHeader, Loading } from "@/components/ds";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { localCalendarDateIso } from "@/lib/format-date";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import type { ServiceBookingListItem, ServiceBookingStatus } from "@/lib/db-service/service-bookings";
import type { ServiceProfessional } from "@/lib/db-service/service-professionals";
import type { ServiceCatalogItem } from "@/lib/db-service/service-catalog";
import { NewBookingDialog } from "@/components/services/NewBookingDialog";
import { RescheduleBookingDialog } from "@/components/services/RescheduleBookingDialog";

export const Route = createFileRoute("/app/agenda")({
  component: AgendaPage,
  head: () => ({ meta: [{ title: "Agenda" }] }),
});

const HOURS = Array.from({ length: 13 }, (_, i) => i + 8); // 08:00–20:00

const STATUS_STYLE: Record<ServiceBookingStatus, string> = {
  pending: "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/50 dark:text-amber-200 dark:border-amber-800",
  confirmed: "bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/50 dark:text-emerald-200 dark:border-emerald-800",
  completed: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800/50 dark:text-slate-300 dark:border-slate-700",
  cancelled: "bg-rose-100 text-rose-800 border-rose-300 line-through dark:bg-rose-950/50 dark:text-rose-300 dark:border-rose-800",
  no_show: "bg-orange-100 text-orange-900 border-orange-300 dark:bg-orange-950/50 dark:text-orange-200 dark:border-orange-800",
  rescheduled: "bg-sky-100 text-sky-900 border-sky-300 dark:bg-sky-950/50 dark:text-sky-200 dark:border-sky-800",
  expired: "bg-muted text-muted-foreground border-border",
};

const STATUS_LABEL: Record<ServiceBookingStatus, string> = {
  pending: "Pendiente",
  confirmed: "Confirmado",
  completed: "Completado",
  cancelled: "Cancelado",
  no_show: "No-show",
  rescheduled: "Reprogramado",
  expired: "Expirado",
};

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return localCalendarDateIso(d);
}

/** Lunes de la semana que contiene `iso` (semana visual Lun-Dom, independiente del day_of_week 0=Dom usado en horarios). */
function mondayOf(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  const jsDay = d.getDay(); // 0=Dom..6=Sáb
  const diffToMonday = jsDay === 0 ? -6 : 1 - jsDay;
  d.setDate(d.getDate() + diffToMonday);
  return localCalendarDateIso(d);
}

function weekDayLabel(iso: string): { short: string; num: string } {
  const d = new Date(`${iso}T00:00:00`);
  return {
    short: d.toLocaleDateString("es-AR", { weekday: "short" }).replace(".", ""),
    num: String(d.getDate()),
  };
}

function AgendaPage() {
  const tenantId = useWorkspaceTenantId();
  const [viewMode, setViewMode] = useState<"day" | "week">("day");
  const [anchorDate, setAnchorDate] = useState(() => localCalendarDateIso());
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState<ServiceBookingListItem[]>([]);
  const [professionals, setProfessionals] = useState<ServiceProfessional[]>([]);
  const [services, setServices] = useState<ServiceCatalogItem[]>([]);
  const [professionalId, setProfessionalId] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [detail, setDetail] = useState<ServiceBookingListItem | null>(null);
  const [rescheduling, setRescheduling] = useState<ServiceBookingListItem | null>(null);

  const days = useMemo(() => {
    if (viewMode === "day") return [anchorDate];
    const monday = mondayOf(anchorDate);
    return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  }, [viewMode, anchorDate]);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [b, p, s] = await Promise.all([
        apiGetJson<{ bookings: ServiceBookingListItem[] }>(
          `/api/service-bookings?dateFrom=${days[0]}&dateTo=${days[days.length - 1]}`,
          tenantId,
        ),
        apiGetJson<{ professionals: ServiceProfessional[] }>("/api/service-bookings?resource=professionals", tenantId),
        apiGetJson<{ services: ServiceCatalogItem[] }>("/api/service-bookings?resource=services", tenantId),
      ]);
      setBookings(b.bookings ?? []);
      setProfessionals((p.professionals ?? []).filter((pr) => pr.active));
      setServices(s.services ?? []);
    } finally {
      setLoading(false);
    }
  }, [tenantId, days]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(
    () => (professionalId ? bookings.filter((b) => b.professionalId === professionalId) : bookings),
    [bookings, professionalId],
  );

  const todayStr = localCalendarDateIso();
  const yearMonth = todayStr.slice(0, 7);
  const kpis = useMemo(
    () => ({
      hoy: bookings.filter((b) => b.bookingDate === todayStr).length,
      pendientes: bookings.filter((b) => b.status === "pending").length,
      esteMes: bookings.filter((b) => b.bookingDate.startsWith(yearMonth) && (b.status === "confirmed" || b.status === "completed")).length,
    }),
    [bookings, todayStr, yearMonth],
  );

  async function patchStatus(id: string, next: ServiceBookingStatus) {
    if (!tenantId) return;
    try {
      await apiSendJson(`/api/service-bookings?id=${id}`, "PATCH", { status: next }, tenantId);
      toast.success("Turno actualizado");
      setDetail(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo actualizar");
    }
  }

  return (
    <ClinicalPageShell offset="none" className="pt-6">
      <MerchantExperienceBar screenId="agenda" />
      <CrudShellHeader
        title="Agenda"
        actions={
          <Button size="sm" className="gap-2" onClick={() => setNewOpen(true)}>
            <Plus className="h-4 w-4" /> Nuevo turno
          </Button>
        }
      />

      <div className="grid grid-cols-3 gap-2 px-4 pb-4 sm:max-w-md">
        <div className="rounded-lg border bg-card p-3">
          <p className="text-[11px] uppercase text-muted-foreground">Turnos hoy</p>
          <p className="text-xl font-semibold">{kpis.hoy}</p>
        </div>
        <div className="rounded-lg border bg-card p-3">
          <p className="text-[11px] uppercase text-muted-foreground">Pendientes</p>
          <p className="text-xl font-semibold text-amber-600">{kpis.pendientes}</p>
        </div>
        <div className="rounded-lg border bg-card p-3">
          <p className="text-[11px] uppercase text-muted-foreground">Este mes</p>
          <p className="text-xl font-semibold text-emerald-600">{kpis.esteMes}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 px-4 pb-4">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" onClick={() => setAnchorDate((d) => addDays(d, viewMode === "day" ? -1 : -7))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => setAnchorDate(localCalendarDateIso())}>
            Hoy
          </Button>
          <Button size="icon" variant="outline" onClick={() => setAnchorDate((d) => addDays(d, viewMode === "day" ? 1 : 7))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            {viewMode === "day"
              ? new Date(`${anchorDate}T00:00:00`).toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" })
              : `Semana del ${days[0]} al ${days[6]}`}
          </span>
        </div>
        <div className="flex items-center gap-1 rounded-lg border p-0.5">
          <button
            type="button"
            onClick={() => setViewMode("day")}
            className={cn("rounded-md px-3 py-1 text-xs font-medium", viewMode === "day" ? "bg-primary text-primary-foreground" : "text-muted-foreground")}
          >
            Día
          </button>
          <button
            type="button"
            onClick={() => setViewMode("week")}
            className={cn("rounded-md px-3 py-1 text-xs font-medium", viewMode === "week" ? "bg-primary text-primary-foreground" : "text-muted-foreground")}
          >
            Semana
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 px-4 pb-4">
        <button
          type="button"
          onClick={() => setProfessionalId("")}
          className={cn("rounded-full px-3 py-1 text-xs font-medium", !professionalId ? "bg-primary text-primary-foreground" : "border text-muted-foreground")}
        >
          Todos
        </button>
        {professionals.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setProfessionalId(p.id)}
            className={cn("rounded-full px-3 py-1 text-xs font-medium", professionalId === p.id ? "bg-primary text-primary-foreground" : "border text-muted-foreground")}
          >
            {p.name}
          </button>
        ))}
      </div>

      {loading ? (
        <Loading />
      ) : (
        <div className="overflow-x-auto px-4 pb-8">
          <div
            className="grid min-w-[640px]"
            style={{ gridTemplateColumns: `64px repeat(${days.length}, minmax(0, 1fr))` }}
          >
            <div />
            {days.map((d) => {
              const { short, num } = weekDayLabel(d);
              const isToday = d === todayStr;
              return (
                <div key={d} className={cn("border-b px-2 py-2 text-center text-xs font-medium uppercase", isToday && "text-primary")}>
                  {short} <span className={cn("ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full", isToday && "bg-primary text-primary-foreground")}>{num}</span>
                </div>
              );
            })}
            {HOURS.map((h) => {
              const hourStr = String(h).padStart(2, "0");
              return (
                <FragmentRow key={h}>
                  <div className="border-b border-r px-1 py-3 text-right text-[11px] text-muted-foreground">
                    {hourStr}:00
                  </div>
                  {days.map((d) => {
                    const cellBookings = visible.filter(
                      (b) => b.bookingDate === d && b.startTime.startsWith(`${hourStr}:`),
                    );
                    return (
                      <div key={d} className="min-h-[52px] space-y-1 border-b border-r p-1">
                        {cellBookings.map((b) => (
                          <button
                            key={b.id}
                            type="button"
                            onClick={() => setDetail(b)}
                            className={cn(
                              "block w-full rounded-md border px-1.5 py-1 text-left text-[11px] leading-tight",
                              STATUS_STYLE[b.status],
                            )}
                          >
                            <span className="font-semibold">{b.startTime}</span> {b.customerName}
                            <br />
                            <span className="opacity-80">{b.serviceName ?? "—"} · {b.professionalName ?? "—"}</span>
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </FragmentRow>
              );
            })}
          </div>
        </div>
      )}

      {tenantId ? (
        <NewBookingDialog
          open={newOpen}
          onOpenChange={setNewOpen}
          tenantId={tenantId}
          professionals={professionals}
          services={services}
          defaultProfessionalId={professionalId || undefined}
          defaultDate={viewMode === "day" ? anchorDate : undefined}
          onCreated={() => void load()}
        />
      ) : null}

      {tenantId ? (
        <RescheduleBookingDialog
          open={!!rescheduling}
          onOpenChange={(v) => !v && setRescheduling(null)}
          tenantId={tenantId}
          booking={rescheduling}
          onRescheduled={() => {
            setRescheduling(null);
            setDetail(null);
            void load();
          }}
        />
      ) : null}

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{detail?.customerName}</DialogTitle>
          </DialogHeader>
          {detail ? (
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">
                {detail.serviceName ?? "—"} · {detail.professionalName ?? "—"}
              </p>
              <p>{detail.bookingDate} · {detail.startTime}–{detail.endTime}</p>
              <p>Estado: <span className="font-medium">{STATUS_LABEL[detail.status]}</span></p>
              {detail.customerPhone ? <p className="text-xs text-muted-foreground">{detail.customerPhone}</p> : null}
              {detail.notes ? <p className="text-xs italic text-muted-foreground">"{detail.notes}"</p> : null}
              <div className="flex flex-wrap gap-1.5 pt-2">
                {detail.status === "pending" && (
                  <Button size="sm" variant="outline" onClick={() => void patchStatus(detail.id, "confirmed")}>
                    Confirmar
                  </Button>
                )}
                {(detail.status === "pending" || detail.status === "confirmed" || detail.status === "rescheduled") && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => void patchStatus(detail.id, "completed")}>
                      Completar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRescheduling(detail)}>
                      Reprogramar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => void patchStatus(detail.id, "no_show")}>
                      No-show
                    </Button>
                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => void patchStatus(detail.id, "cancelled")}>
                      Cancelar
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </ClinicalPageShell>
  );
}

/** React exige un solo elemento raíz por celda del grid; helper para no repetir <> en cada fila de horas. */
function FragmentRow({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
